import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ordersClient, type Order } from '@/lib/orders-client';
import { conversationClient } from '@/lib/gupshup-client';

interface ConfirmOrderDialogProps {
    order: Order | null;
    onConfirm: (orderId: string) => void;
    onCancel: () => void;
}

export function ConfirmOrderDialog({ order, onConfirm, onCancel }: ConfirmOrderDialogProps) {
    const { t } = useTranslation('orders');
    const [message, setMessage] = useState('');
    const [sendMessage, setSendMessage] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const phone = order?.customer?.phone;

    useEffect(() => {
        if (order) {
            const defaultMessage =
                order.deliveryType === 'delivery'
                    ? t('confirmDialog.defaultMessageDelivery', {
                          minutes: order.estimatedDeliveryMinutes ?? 30,
                      })
                    : t('confirmDialog.defaultMessagePickup');
            setMessage(defaultMessage);
            setError(null);
        }
    }, [order, t]);

    const handleConfirm = async () => {
        if (!order) return;
        setLoading(true);
        setError(null);

        try {
            await ordersClient.updateStatus(order.id, 'confirmed');

            if (sendMessage && phone && message.trim()) {
                try {
                    await conversationClient.messages.sendText({ to: phone, body: message.trim(), role: 'assistant' });
                } catch (msgErr) {
                    console.error('Failed to send WhatsApp message:', msgErr);
                    toast.error(t('confirmDialog.confirmedMessageFailed'));
                    onConfirm(order.id);
                    return;
                }
            }

            toast.success(
                sendMessage ? t('confirmDialog.confirmed') : t('confirmDialog.confirmedNoMessage'),
            );
            onConfirm(order.id);
        } catch (err) {
            console.error('Failed to confirm order:', err);
            setError(t('confirmDialog.error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={!!order} onOpenChange={(open) => !open && onCancel()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('confirmDialog.title')}</DialogTitle>
                    <DialogDescription>{t('confirmDialog.description')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    {!phone && (
                        <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded-md p-2.5">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{t('confirmDialog.noPhone')}</span>
                        </div>
                    )}

                    {phone && (
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={sendMessage}
                                onChange={(e) => setSendMessage(e.target.checked)}
                                className="rounded border-border"
                            />
                            <span className="font-medium text-foreground">{t('confirmDialog.sendMessage')}</span>
                        </label>
                    )}

                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        disabled={!phone || !sendMessage}
                        placeholder={t('confirmDialog.messagePlaceholder')}
                    />

                    {error && (
                        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-md p-2.5">{error}</div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onCancel} disabled={loading}>
                        {t('confirmDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={loading}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {sendMessage && phone
                            ? t('confirmDialog.confirmAndSend')
                            : t('confirmDialog.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
